const coordinationError = 'Account changes are unavailable in this browser window. Use a secure browser window with site data enabled, then try again.'
const browserAuthorities = new WeakMap<Window, Map<string, SessionAuthority>>()
const pendingEpochPrefix = '!'

export type AdvanceSessionEpoch = (phase?: 'pending') => string

interface SessionAuthority {
  snapshot(): Promise<string | null>
  isCurrent(epoch: string | null): boolean
  write<T>(write: (advanceEpoch: AdvanceSessionEpoch) => Promise<T>, signal?: AbortSignal | null): Promise<T>
  refresh(epoch: string, refresh: () => Promise<boolean>): Promise<boolean>
}

function createAuthority(browser?: Window, apiOrigin?: string): SessionAuthority {
  let cookieWrites = Promise.resolve()
  let localEpoch = 0
  const pendingRefreshes = new Map<string, Promise<boolean>>()
  const key = `badland-session-epoch:${apiOrigin}`
  const lockName = `badland-session-cookies:${apiOrigin}`

  function currentEpoch(): string | null {
    return browser ? browser.localStorage.getItem(key) : String(localEpoch)
  }

  function storeEpoch(epoch: string): string {
    if (!browser) return String(++localEpoch)
    try {
      browser.localStorage.setItem(key, epoch)
      if (browser.localStorage.getItem(key) !== epoch) throw new Error(coordinationError)
      return epoch
    } catch (error) {
      throw Object.assign(new Error(coordinationError), { cause: error })
    }
  }

  function advanceEpoch(phase?: 'pending'): string {
    const epoch = browser ? browser.crypto.randomUUID() : ''
    // Replace one UUID character so beginning a write needs no extra storage.
    return storeEpoch(browser && phase === 'pending' ? `${pendingEpochPrefix}${epoch.slice(1)}` : epoch)
  }

  function queue<T>(write: (advance: AdvanceSessionEpoch) => Promise<T>, signal?: AbortSignal | null): Promise<T> {
    const request = cookieWrites.then(async () => {
      if (signal?.aborted) throw signal.reason
      if (!browser) return write(advanceEpoch)
      // Preserve interactive sign-in on browsers without Web Locks. Their
      // requests receive no replay authority because other tabs cannot join.
      if (!browser.navigator.locks) return write(() => String(++localEpoch))
      let admitted = false
      try {
        const locks = browser.navigator.locks
        if (!locks) throw new Error(coordinationError)
        return await locks.request(lockName, { mode: 'exclusive', ...(signal ? { signal } : {}) }, async () => {
          // Prove durable storage admission before any response can write cookies.
          storeEpoch(currentEpoch() ?? browser.crypto.randomUUID())
          admitted = true
          return write(advanceEpoch)
        })
      } catch (error) {
        if (signal?.aborted || admitted) throw error
        throw Object.assign(new Error(coordinationError), { cause: error })
      }
    })
    cookieWrites = request.then(() => undefined, () => undefined)
    return request
  }

  return {
    async snapshot() {
      if (browser && !browser.navigator.locks) return null
      try {
        const epoch = currentEpoch() ?? await queue(async () => currentEpoch())
        // Never grant replay authority while cookies may still change, even if
        // a failed settlement leaves its admission marker in durable storage.
        return epoch?.startsWith(pendingEpochPrefix) ? null : epoch
      } catch {
        // Ordinary RPCs still run; this admission cannot refresh or be replayed.
        return null
      }
    },
    isCurrent(epoch) {
      if (browser && !browser.navigator.locks) return false
      try {
        return epoch !== null && !epoch.startsWith(pendingEpochPrefix) && currentEpoch() === epoch
      } catch {
        return false
      }
    },
    write: queue,
    refresh(epoch, refresh) {
      const pending = pendingRefreshes.get(epoch)
      if (pending) return pending
      const request = refresh().finally(() => {
        if (pendingRefreshes.get(epoch) === request) pendingRefreshes.delete(epoch)
      })
      pendingRefreshes.set(epoch, request)
      return request
    },
  }
}

export function getSessionAuthority(baseUrl: string): SessionAuthority {
  // Server factories can have unrelated cookie jars, so never share SSR state.
  if (typeof window === 'undefined') return createAuthority()
  const apiOrigin = new URL(baseUrl, window.location.href).origin
  let authorities = browserAuthorities.get(window)
  if (!authorities) {
    authorities = new Map()
    browserAuthorities.set(window, authorities)
  }
  let authority = authorities.get(apiOrigin)
  if (!authority) {
    authority = createAuthority(window, apiOrigin)
    authorities.set(apiOrigin, authority)
  }
  return authority
}
